import axios, { AxiosError, AxiosRequestConfig } from 'axios';

import { BASE_URL, getSubmissionBackendUrl } from '../constants/api-urls';
import { getAuth0AccessToken } from './auth0Session';
import {
  ensureFreshAuth0AccessToken,
  handleAuth0SessionExpired,
  refreshAuth0AccessToken,
} from './auth0TokenRefresh';

// The curator backend reads the Auth0 token from this header only; Authorization
// and access_token keep carrying Spotify tokens for existing endpoints.
const AUTH0_TOKEN_HEADER = 'x-auth0-token';

type RetryableConfig = AxiosRequestConfig & { __auth0Retry?: boolean };

const getCallbackHashToken = (): string | null => {
  if (window.location.pathname !== '/shift') return null;
  const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : '';
  return new URLSearchParams(hash).get('access_token')?.trim() || null;
};

export const getCuratorAuthHeader = (): Record<string, string> => {
  const token = getCallbackHashToken() || getAuth0AccessToken();
  return token ? { [AUTH0_TOKEN_HEADER]: token } : {};
};

const isAuth0ProtectedUrl = (url?: string) => {
  if (!url) {
    return false;
  }
  const submission = getSubmissionBackendUrl();
  return Boolean((BASE_URL && url.startsWith(BASE_URL)) || (submission && url.startsWith(submission)));
};

const isAuth0RefreshUrl = (url?: string) => Boolean(url?.includes('/auth0/refresh'));

const applyAuth0Headers = (config: RetryableConfig) => {
  const token = getAuth0AccessToken();
  if (!token) {
    return config;
  }

  if (isAuth0ProtectedUrl(config.url)) {
    config.headers = {
      ...config.headers,
      ...getCuratorAuthHeader(),
      Authorization: `Bearer ${token}`,
    };
  }

  return config;
};

export const installCuratorAuthHeader = () => {
  axios.interceptors.request.use(async config => {
    if (isAuth0RefreshUrl(config.url)) {
      return config;
    }

    if (isAuth0ProtectedUrl(config.url)) {
      await ensureFreshAuth0AccessToken();
      return applyAuth0Headers(config);
    }

    if (BASE_URL && config.url?.startsWith(BASE_URL)) {
      config.headers = { ...config.headers, ...getCuratorAuthHeader() };
    }

    return config;
  });

  axios.interceptors.response.use(
    response => response,
    async (error: AxiosError) => {
      const config = error.config as RetryableConfig | undefined;
      if (!config || config.__auth0Retry || error.response?.status !== 401) {
        return Promise.reject(error);
      }

      if (!isAuth0ProtectedUrl(config.url) || isAuth0RefreshUrl(config.url)) {
        return Promise.reject(error);
      }

      try {
        await refreshAuth0AccessToken();
        config.__auth0Retry = true;
        return axios(applyAuth0Headers(config));
      } catch {
        handleAuth0SessionExpired();
        return Promise.reject(error);
      }
    }
  );
};
